import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  useToast
} from '@chakra-ui/react'
import { Label } from '@sequencehq/forms'
import { noReturn } from 'lib/noReturn'
import { Field, Form } from 'react-final-form'
import { GreyGrey30, Lato13Regular, RedRed70 } from '@sequencehq/design-tokens'
import { FORM_ERROR } from 'final-form'
import { Toast } from 'components/TeamSettings/common/Toast'
import { composeValidators, isEmail } from '@sequencehq/validation'
import { useTeamSettings } from 'components/TeamSettings/hooks/useTeamSettings'
import { dashboard20240730Client } from '@sequencehq/api/dist/clients/dashboard/v20240730'
import {
  ErrorResponse,
  isSequenceApiError,
  isSequenceApiErrorResponse,
  SequenceError,
  useMutation
} from '@sequencehq/api'

interface FormValues {
  memberEmail: string
}

interface InviteMemberFormProps {
  onCancel: () => void
}

const isGenericError = (error: unknown): error is Error =>
  !!error && typeof error === 'object' && 'cause' in error

const extractValidationErrors = (
  errorResponse: ErrorResponse
): Array<SequenceError> =>
  isSequenceApiErrorResponse(errorResponse) &&
  errorResponse.error.type == 'validationError'
    ? errorResponse.error.errors.filter(err => err.code === 'validationError')
    : []

export const InviteMemberForm = ({ onCancel }: InviteMemberFormProps) => {
  const { setAction, refetchMembers } = useTeamSettings()
  const toast = useToast()
  const inviteMutation = useMutation(
    dashboard20240730Client.postSequenceAccountInvite
  )

  const onSubmit = async (values: FormValues) => {
    try {
      // If the request returns a non-2xx code, the mutateAsync promise will be rejected and trigger the catch handler
      await inviteMutation.mutateAsync({
        email: values.memberEmail
      })

      toast({
        position: 'bottom-left',
        isClosable: false,
        render: () => (
          <Toast
            type="success"
            title="Invitation sent!"
            description={`Invitation was sent to ${values.memberEmail}`}
            isClosable
          />
        )
      })
      refetchMembers()
      setAction('view')
    } catch (e) {
      const errorResponse = isSequenceApiError(e) ? e.response : undefined
      const validationErrors = isSequenceApiErrorResponse(errorResponse)
        ? extractValidationErrors(errorResponse)
        : []

      if (
        // We have to re-check the error response type here for TS to infer correctly inside the block
        isSequenceApiErrorResponse(errorResponse) &&
        validationErrors.length > 0
      ) {
        const emailDuplicated = validationErrors.some(
          err => err.message === 'Email already in use'
        )

        if (emailDuplicated) {
          return {
            [FORM_ERROR]: `${values.memberEmail} has already joined this account.`
          }
        }
      }

      const errorMessage =
        isSequenceApiError(e) && isSequenceApiErrorResponse(e.response)
          ? e.response.error.message
          : isGenericError(e)
            ? e.message
            : 'Please try again'

      toast({
        position: 'bottom-left',
        isClosable: false,
        render: () => (
          <Toast
            type="error"
            title="An error occurred"
            description={errorMessage}
            isClosable
          />
        )
      })
      setAction('view')
    }
  }

  return (
    <Form<FormValues>
      destroyOnUnregister
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting, submitError }) => {
        return (
          <>
            <Flex
              id="invite-member-form"
              as="form"
              p={6}
              flexDirection="column"
              onSubmit={noReturn(handleSubmit)}
            >
              <Field
                name="memberEmail"
                validate={composeValidators(isEmail)}
                render={({
                  input,
                  meta: { modifiedSinceLastSubmit, error }
                }) => (
                  <FormControl
                    isInvalid={
                      (error || submitError) && !modifiedSinceLastSubmit
                    }
                  >
                    <Label htmlFor="memberEmail">Send an invite to</Label>
                    <Input
                      data-testid="settings.team.invite.email"
                      id="memberEmail"
                      size="sm"
                      variant="formInput2"
                      placeholder="Ex: freddie@example.com"
                      type="email"
                      required
                      disabled={submitting}
                      {...input}
                    />
                    {submitError && !modifiedSinceLastSubmit && (
                      <FormErrorMessage {...Lato13Regular} color={RedRed70}>
                        {submitError}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                )}
              />
            </Flex>
            <Flex
              p={3}
              justifyContent="end"
              gap={2}
              border={`1px solid ${GreyGrey30}`}
            >
              <Button variant="secondary2" size="sm" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                data-testid="settings.team.invite.send"
                variant="primary2"
                size="sm"
                type="submit"
                form="invite-member-form"
                isDisabled={submitting}
              >
                Send invite
              </Button>
            </Flex>
          </>
        )
      }}
    />
  )
}
